import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { X, ArrowUp, ArrowDown, GripVertical, PencilSquare, Trash, ChevronDown, ChevronRight } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useJournalTemplateBuilder } from '../../hooks/useJournalTemplateBuilder';
import JournalPromptModal from '../JournalPromptModal';
import JournalTemplateSelector from './JournalTemplateSelector';
import ThemedSelect from '../shared/ThemedSelect';

const TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'datetime', label: 'Date/Time' },
  { value: 'rank', label: 'Rank 1-5' },
  { value: 'single', label: 'Single Select' },
  { value: 'multi', label: 'Multi Select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
];

function fieldTypeLabel(f) {
  if (f.type === 'text') return 'Text';
  if (f.type === 'datetime') return 'Date/Time';
  if (f.type === 'rank') return `Rank 1-${f.max || 5}`;
  if (f.type === 'single') return 'Single select';
  if (f.type === 'multi') return 'Multi select';
  if (f.type === 'checkbox') return 'Checkbox';
  if (f.type === 'number') return 'Number';
  if (f.type === 'currency') return 'Currency';
  return f.type || 'Text';
}

function optionsSummary(f) {
  const n = (f.options || []).length;
  if (f.type !== 'single' && f.type !== 'multi') return null;
  if (n === 0) return 'Add options…';
  return n === 1 ? '1 option — edit' : `${n} options — edit`;
}

/**
 * Full journal template selector + prompt builder (shared by Settings and dashboard).
 * @param {object} [props.seedTemplate] - start editing this template
 * @param {boolean} [props.hideSelector] - hide the "select template" card
 * @param {boolean} [props.autoSave=true]
 * @param {boolean} [props.autoLoadSaved=true]
 * @param {boolean} [props.showExplicitSave=false] - Save / Back footer (for modal flow)
 * @param {Function} [props.onSaveSuccess]
 * @param {Function} [props.onCancel]
 * @param {Function} [props.onTemplateChange] - (template, name) as the user edits
 */
const JournalTemplateBuilder = ({
  seedTemplate = null,
  hideSelector = false,
  autoSave = true,
  autoLoadSaved = true,
  showExplicitSave = false,
  onSaveSuccess,
  onCancel,
  onTemplateChange = null,
} = {}) => {
  const { colors } = useTheme();
  const {
    isMobile,
    journalTemplate,
    templateName,
    setTemplateName,
    optionInputs,
    setOptionInputs,
    editingOption,
    setEditingOption,
    dragIndex,
    setDragIndex,
    dragOverState,
    setDragOverState,
    promptModal,
    setPromptModal,
    handleSelectTemplate,
    saveNow,
    saving,
    addField,
    updateField,
    removeField,
    handleSavePrompt,
    handleEditPrompt,
    addOptionToken,
    removeOptionToken,
    beginEditOption,
    commitEditOption,
    cancelEditOption,
    moveField,
    reorderFields,
  } = useJournalTemplateBuilder({
    seedTemplate,
    autoSave: showExplicitSave ? false : autoSave,
    autoLoadSaved: seedTemplate ? false : autoLoadSaved,
    saveOnSelect: !showExplicitSave,
    onTemplateChange,
  });
  const hasPrompts = (journalTemplate.fields || []).length > 0;
  const [expandedFields, setExpandedFields] = useState(() => new Set());

  const fieldExpandKey = (f, idx) => f.key || `idx_${idx}`;

  const toggleFieldExpanded = (key) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleAddField = () => {
    const nextNumber = (journalTemplate.fields || []).length + 1;
    const newKey = `field_${nextNumber}`;
    addField();
    setExpandedFields((prev) => new Set(prev).add(newKey));
  };

  const handleExplicitSave = async () => {
    try {
      const result = await saveNow();
      onSaveSuccess?.(result);
    } catch (_) {
      // message already shown
    }
  };

  return (
    <>
      {/* Template Selector - Simplified */}
      {!hideSelector && (
      <div style={{ 
        backgroundColor: colors.bgCard, 
        border: `1px solid ${colors.border}`, 
        borderRadius: '8px', 
        padding: '1rem', 
        marginBottom: '1.5rem' 
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div>
            <p style={{ color: colors.text, fontSize: '0.9rem', marginBottom: '0.25rem', fontWeight: 500 }}>
              Template: {templateName || 'Custom'}
            </p>
            <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
              Start with a default template or build your own
            </p>
          </div>
          <JournalTemplateSelector
            onSelectTemplate={handleSelectTemplate}
            currentTemplateName={templateName}
            showCard={false}
          />
        </div>
        <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0, paddingTop: '0.75rem', borderTop: `1px solid ${colors.border}` }}>
          Add prompts below to create the questions you'll answer for each trade. 
          For each prompt, set the input type (text, number, rank, etc.) and any options if needed. 
          Your prompts will appear when journaling trades.
        </p>
      </div>
      )}

      {hideSelector && (
        <div style={{ marginBottom: '1rem' }}>
          <Form.Label style={{ color: colors.textMuted, fontSize: '0.75rem', marginBottom: 4 }}>
            Template name
          </Form.Label>
          <Form.Control
            size="sm"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            style={{
              background: colors.bgInput || colors.bgCard,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              maxWidth: 320,
            }}
          />
        </div>
      )}
      
      {isMobile ? (
        /* Mobile: Vertical card layout */
        <>
          {(journalTemplate.fields || []).map((f, idx) => {
            const totalFields = (journalTemplate.fields || []).length;
            const getTypeLabel = () => {
              if (f.type === 'text') return 'Text';
              if (f.type === 'datetime') return 'Date/Time';
              if (f.type === 'rank') return `Rank 1-${f.max || 5}`;
              if (f.type === 'single') return 'Single Select';
              if (f.type === 'checkbox') return 'Checkbox';
              if (f.type === 'number') return 'Number';
              if (f.type === 'currency') return 'Currency';
              return f.type;
            };

            return (
              <div
                key={f.key || idx}
                style={{
                  backgroundColor: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}
              >
                {/* Card Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <div style={{ color: colors.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Prompt {idx + 1}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {/* Up Arrow */}
                    <button
                      title="Move up"
                      aria-label="Move up"
                      onClick={() => moveField(idx, 'up')}
                      disabled={idx === 0}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        color: idx === 0 ? colors.textMuted : colors.text,
                        cursor: idx === 0 ? 'not-allowed' : 'pointer',
                        padding: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: idx === 0 ? 0.4 : 1
                      }}
                    >
                      <ArrowUp size={18} />
                    </button>
                    {/* Down Arrow */}
                    <button
                      title="Move down"
                      aria-label="Move down"
                      onClick={() => moveField(idx, 'down')}
                      disabled={idx === totalFields - 1}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        color: idx === totalFields - 1 ? colors.textMuted : colors.text,
                        cursor: idx === totalFields - 1 ? 'not-allowed' : 'pointer',
                        padding: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: idx === totalFields - 1 ? 0.4 : 1
                      }}
                    >
                      <ArrowDown size={18} />
                    </button>
                    {/* Edit Button */}
                    <button
                      title="Edit"
                      aria-label="Edit"
                      onClick={() => handleEditPrompt(idx)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        color: colors.primary,
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PencilSquare size={18} />
                    </button>
                    {/* Delete Button */}
                    <button
                      title="Delete"
                      aria-label="Delete"
                      onClick={() => removeField(idx)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        color: colors.danger,
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </div>

                {/* Label and Type on same line */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '120px' }}>
                    <div style={{ color: colors.text, fontSize: '1.05rem', fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.35 }}>
                      {f.label || <span style={{ color: colors.textMuted, fontStyle: 'italic', fontWeight: 500 }}>Untitled</span>}
                    </div>
                  </div>
                  <span style={{ 
                    color: colors.textMuted, 
                    fontSize: '0.8rem',
                    display: 'inline-block'
                  }}>
                    {getTypeLabel()}
                  </span>
                </div>

                {(f.type === 'single' || f.type === 'multi') && (
                  <div style={{ color: colors.primary, fontSize: '0.8rem', fontStyle: 'italic', opacity: 0.9 }}>
                    {optionsSummary(f)}
                  </div>
                )}
              </div>
            );
          })}
          <div
            style={{
              marginTop: 12,
              display: 'flex',
              justifyContent: hasPrompts ? 'flex-end' : 'center',
            }}
          >
            <Button 
              type="button"
              variant="outline-primary" 
              size="sm" 
              onClick={addField} 
              style={{
                borderColor: colors.primary,
                color: colors.primary,
                width: hasPrompts ? 'auto' : '100%',
                maxWidth: hasPrompts ? 'none' : '280px',
                fontWeight: 500,
              }}
            >
              {hasPrompts ? '+ Journal Prompt' : 'Build Journal'}
            </Button>
          </div>
        </>
      ) : (
        /* Desktop: Card-based layout */
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
            {(journalTemplate.fields || []).map((f, idx) => (
              <div
                key={f.key || idx}
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => {
                  e.preventDefault();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const midY = rect.top + rect.height / 2;
                  const position = (e.clientY < midY) ? 'above' : 'below';
                  setDragOverState({ index: idx, position });
                }}
                onDragLeave={() => setDragOverState({ index: null, position: null })}
                onDrop={() => {
                  const targetIdx = dragOverState.index;
                  const insertIdx = (dragOverState.position === 'below') ? targetIdx + 1 : targetIdx;
                  let fromIdx = dragIndex;
                  let toIdx = insertIdx;
                  if (fromIdx < toIdx) {
                    toIdx = toIdx - 1;
                  }
                  reorderFields(fromIdx, toIdx);
                  setDragIndex(null);
                  setDragOverState({ index: null, position: null });
                }}
                style={{ 
                  backgroundColor: colors.bgSecondary,
                  border: `1px solid ${dragOverState.index === idx ? colors.primary : colors.border}`,
                  borderRadius: '12px',
                  padding: '0.85rem 1rem',
                  transition: 'border-color 0.15s ease, opacity 0.15s ease',
                  boxShadow: dragOverState.index === idx ? `0 4px 16px ${colors.primary}18` : 'none',
                  opacity: dragIndex === idx ? 0.5 : 1
                }}
              >
                {(() => {
                  const expandKey = fieldExpandKey(f, idx);
                  const expanded = expandedFields.has(expandKey);
                  const optSummary = optionsSummary(f);
                  const needsOptions = f.type === 'single' || f.type === 'multi';
                  return (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'grab',
                          color: colors.textMuted,
                          padding: '0.35rem',
                          marginTop: '0.35rem',
                          borderRadius: '6px',
                          flexShrink: 0,
                        }}
                        title="Drag to reorder"
                      >
                        <GripVertical size={18} />
                      </div>

                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: expanded ? '0.75rem' : '0.35rem' }}>
                        {/* Title: title-size fill, not boxed input */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                          <input
                            value={f.label || ''}
                            placeholder="Prompt title…"
                            onChange={(e) => updateField(idx, { label: e.target.value })}
                            aria-label="Prompt title"
                            style={{
                              flex: 1,
                              minWidth: 0,
                              background: 'transparent',
                              border: 'none',
                              borderBottom: `1px solid transparent`,
                              borderRadius: 0,
                              outline: 'none',
                              color: colors.text,
                              fontSize: '1.15rem',
                              fontWeight: 600,
                              lineHeight: 1.35,
                              padding: '0.15rem 0 0.35rem',
                              boxShadow: 'none',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderBottomColor = colors.primary + '55';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderBottomColor = 'transparent';
                            }}
                          />
                          <button
                            type="button"
                            title="Remove prompt"
                            aria-label="Remove prompt"
                            onClick={() => removeField(idx)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: colors.textMuted,
                              cursor: 'pointer',
                              padding: '0.35rem',
                              borderRadius: '6px',
                              display: 'inline-flex',
                              flexShrink: 0,
                              marginTop: '0.1rem',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = colors.danger + '20';
                              e.currentTarget.style.color = colors.danger;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = colors.textMuted;
                            }}
                          >
                            <X size={16} />
                          </button>
                        </div>

                        {/* Collapsed meta: type + italic options affordance */}
                        {!expanded && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap', paddingLeft: 0 }}>
                            <button
                              type="button"
                              onClick={() => toggleFieldExpanded(expandKey)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.3rem',
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                color: colors.textMuted,
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                              }}
                            >
                              <ChevronRight size={12} />
                              <span>{fieldTypeLabel(f)}</span>
                            </button>
                            {optSummary && (
                              <button
                                type="button"
                                onClick={() => toggleFieldExpanded(expandKey)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  padding: 0,
                                  color: colors.primary,
                                  fontSize: '0.8rem',
                                  fontStyle: 'italic',
                                  cursor: 'pointer',
                                  opacity: (f.options || []).length ? 0.95 : 0.75,
                                }}
                              >
                                {optSummary}
                              </button>
                            )}
                            {!needsOptions && (
                              <button
                                type="button"
                                onClick={() => toggleFieldExpanded(expandKey)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  padding: 0,
                                  color: colors.textMuted,
                                  fontSize: '0.75rem',
                                  fontStyle: 'italic',
                                  cursor: 'pointer',
                                }}
                              >
                                Change type…
                              </button>
                            )}
                          </div>
                        )}

                        {/* Expanded: type select + options editor */}
                        {expanded && (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.75rem',
                              paddingTop: '0.25rem',
                              borderTop: `1px solid ${colors.border}`,
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={() => toggleFieldExpanded(expandKey)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  background: 'transparent',
                                  border: 'none',
                                  padding: 0,
                                  color: colors.textMuted,
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                }}
                              >
                                <ChevronDown size={12} />
                                Type
                              </button>
                              <div style={{ minWidth: '160px', flex: '0 1 200px' }}>
                                <ThemedSelect
                                  value={f.type || 'text'}
                                  options={TYPE_OPTIONS}
                                  onChange={(value) => {
                                    updateField(idx, { type: value });
                                    if (value === 'single' || value === 'multi') {
                                      setExpandedFields((prev) => new Set(prev).add(expandKey));
                                    }
                                  }}
                                  width="100%"
                                />
                              </div>
                              {f.type === 'rank' && (
                                <Form.Control
                                  type="number"
                                  min="2"
                                  max="10"
                                  value={f.max || 5}
                                  onChange={(e) => updateField(idx, { max: parseInt(e.target.value || '5', 10) })}
                                  placeholder="Max"
                                  style={{
                                    backgroundColor: colors.bgInput,
                                    borderColor: colors.border,
                                    color: colors.text,
                                    fontSize: '0.85rem',
                                    padding: '0.4rem 0.6rem',
                                    borderRadius: '8px',
                                    width: '88px',
                                  }}
                                />
                              )}
                            </div>

                            {needsOptions && (
                              <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <Form.Control
                                  value={optionInputs[idx] || ''}
                                  placeholder="Add option…"
                                  onChange={(e) => setOptionInputs((prev) => ({ ...prev, [idx]: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ',') {
                                      e.preventDefault();
                                      addOptionToken(idx, optionInputs[idx]);
                                    }
                                  }}
                                  style={{
                                    backgroundColor: colors.bgInput,
                                    borderColor: colors.border,
                                    color: colors.text,
                                    fontSize: '0.85rem',
                                    padding: '0.4rem 0.65rem',
                                    borderRadius: '8px',
                                    width: '180px',
                                    flexShrink: 0,
                                  }}
                                />
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', flex: 1, alignItems: 'center', minWidth: 0 }}>
                                  {(f.options || []).map((opt, i) => {
                                    const isEditing = editingOption.fieldIdx === idx && editingOption.optIdx === i;
                                    return (
                                      <div
                                        key={i}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '0.4rem',
                                          backgroundColor: colors.primary + '15',
                                          color: colors.primary,
                                          border: `1px solid ${colors.primary}40`,
                                          borderRadius: '8px',
                                          padding: '0.3rem 0.6rem',
                                          fontSize: '0.8rem',
                                          fontWeight: 500,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {isEditing ? (
                                          <input
                                            value={editingOption.value}
                                            onChange={(e) => setEditingOption((prev) => ({ ...prev, value: e.target.value }))}
                                            onBlur={commitEditOption}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') commitEditOption();
                                              if (e.key === 'Escape') cancelEditOption();
                                            }}
                                            autoFocus
                                            style={{
                                              background: 'transparent',
                                              border: 'none',
                                              outline: 'none',
                                              color: colors.primary,
                                              fontSize: '0.8rem',
                                              fontWeight: 500,
                                              minWidth: 60,
                                              maxWidth: 200,
                                            }}
                                          />
                                        ) : (
                                          <span
                                            onClick={() => beginEditOption(idx, i, opt)}
                                            style={{ cursor: 'text', userSelect: 'none' }}
                                          >
                                            {typeof opt === 'string' ? opt : (opt.label || opt.value || String(opt))}
                                          </span>
                                        )}
                                        <button
                                          type="button"
                                          title="Remove option"
                                          aria-label="Remove option"
                                          onClick={() => removeOptionToken(idx, i)}
                                          style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: colors.primary,
                                            cursor: 'pointer',
                                            padding: 0,
                                            display: 'inline-flex',
                                            width: '16px',
                                            height: '16px',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <X size={12} />
                                        </button>
                                      </div>
                                    );
                                  })}
                                  {(f.options || []).length === 0 && (
                                    <span style={{ color: colors.textMuted, fontSize: '0.8rem', fontStyle: 'italic' }}>
                                      No options yet
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: '1rem',
              display: 'flex',
              justifyContent: hasPrompts ? 'flex-end' : 'center',
            }}
          >
            <Button 
              type="button"
              variant="outline-primary" 
              size="sm" 
              onClick={handleAddField} 
              style={{ 
                borderColor: colors.primary, 
                color: colors.primary,
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontWeight: 500,
                transition: 'all 0.2s ease',
                minWidth: hasPrompts ? 'auto' : '220px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary + '10';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {hasPrompts ? '+ Journal Prompt' : 'Build Journal'}
            </Button>
          </div>
        </>
      )}
      <JournalPromptModal
        show={promptModal.show}
        onHide={() => setPromptModal({ show: false, field: null, fieldIndex: null })}
        onSave={handleSavePrompt}
        field={promptModal.field}
        fieldIndex={promptModal.fieldIndex}
      />

      {showExplicitSave && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
            marginTop: 20,
            paddingTop: 16,
            borderTop: `1px solid ${colors.border}`,
          }}
        >
          {onCancel && (
            <Button
              variant="outline-light"
              onClick={onCancel}
              disabled={saving}
              style={{ borderColor: colors.border, color: colors.text, background: 'transparent', borderRadius: 10 }}
            >
              Back
            </Button>
          )}
          <Button
            onClick={handleExplicitSave}
            disabled={saving}
            style={{
              background: colors.primary,
              border: 'none',
              borderRadius: 10,
              fontWeight: 600,
              padding: '8px 18px',
            }}
          >
            {saving ? 'Saving…' : 'Save template'}
          </Button>
        </div>
      )}
    </>
  );
};

export default JournalTemplateBuilder;
